import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  constructor() {
    super();

    const lockQuantityInput = () => {
      const input = this.querySelector('input[ref="quantityInput"]');
      if (!(input instanceof HTMLInputElement)) return;

      input.readOnly = true;
      input.tabIndex = -1;
      input.setAttribute('inputmode', 'none');
      input.setAttribute('aria-readonly', 'true');
    };

    lockQuantityInput();
    this.quantityInputObserver = new MutationObserver(lockQuantityInput);
    this.quantityInputObserver.observe(this, { childList: true, subtree: true });
    setTimeout(lockQuantityInput, 0);
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}


const cartQuantityInputSelector = 'quantity-selector-component input[ref="quantityInput"]';

const lockCartQuantityInput = (input) => {
  if (!(input instanceof HTMLInputElement)) return;
  if (!input.readOnly) input.readOnly = true;
  if (input.tabIndex !== -1) input.tabIndex = -1;
  if (input.getAttribute('inputmode') !== 'none') input.setAttribute('inputmode', 'none');
  if (input.getAttribute('aria-readonly') !== 'true') input.setAttribute('aria-readonly', 'true');
  if (input.style.pointerEvents !== 'none') input.style.pointerEvents = 'none';
  if (input.style.cursor !== 'default') input.style.cursor = 'default';
  if (input.style.userSelect !== 'none') input.style.userSelect = 'none';
};

const lockCartQuantityInputs = (root = document) => {
  if (root instanceof Element && root.matches(cartQuantityInputSelector)) {
    lockCartQuantityInput(root);
  }
  root.querySelectorAll?.(cartQuantityInputSelector).forEach(lockCartQuantityInput);
};

lockCartQuantityInputs();

const cartQuantityInputObserver = new MutationObserver((records) => {
  records.forEach((record) => {
    if (record.type === 'attributes') {
      lockCartQuantityInput(record.target);
      return;
    }
    record.addedNodes.forEach((node) => {
      if (node instanceof Element) lockCartQuantityInputs(node);
    });
  });
});

cartQuantityInputObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['readonly', 'tabindex', 'inputmode', 'aria-readonly', 'style'],
});
